import { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "contexts/AuthContext";
import { API_BASE } from "config/api";

/**
 * Per-member "book" view of tithe or welfare for a given year — which
 * months each member has paid for, and which they owe. Pass `memberId` to
 * scope to a single member (e.g. a member viewing their own book on their
 * dashboard); omit it for the church-wide admin/treasurer view.
 */
const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 350;

export default function useMemberLedgerData(type, memberId) {
  const { church } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const searchTimeoutRef = useRef(null);

  const fetchLedger = useCallback(
    async (targetYear, targetPage, targetSearch) => {
      if (!church?.id) return;
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("authToken");
        const params = new URLSearchParams({
          year: String(targetYear ?? year),
          page: String(targetPage ?? page),
          per_page: String(PER_PAGE),
        });
        const effectiveSearch = targetSearch ?? search;
        if (effectiveSearch) params.set("search", effectiveSearch);
        if (memberId) params.set("member_id", memberId);
        const response = await fetch(
          `${API_BASE}/accounts/ledger/${church.id}/${type}?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || "Failed to load member book");
        }
        const result = await response.json();
        setRows(result.data || []);
        setMeta(result.meta || null);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    },
    [church?.id, type, memberId, year, page, search]
  );

  const handleYearChange = (newYear) => {
    setYear(newYear);
    setPage(1);
    fetchLedger(newYear, 1, search);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchLedger(year, newPage, search);
  };

  const handleSearchChange = (newSearch) => {
    setSearch(newSearch);
    setPage(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchLedger(year, 1, newSearch);
    }, SEARCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    if (church?.id) {
      setPage(1);
      fetchLedger(year, 1, search);
    }
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [church?.id, memberId]);

  return {
    rows,
    meta,
    year,
    page,
    perPage: PER_PAGE,
    search,
    isLoading,
    error,
    fetchLedger,
    handleYearChange,
    handlePageChange,
    handleSearchChange,
  };
}
